import { Link, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { getLanguage, pick } from "../../lib/i18n";
import { CurrentUser } from "../../types";

const copy = {
  pl: { errorLabel: "Błąd", restore: "Przywróć", resolved: "Rozwiązane", stackTrace: "Stack trace", title: "Błędy aplikacji", subtitle: (n: number) => `Zdarzenia błędów zgłoszone administratorowi. Nierozwiązanych: ${n}.`, unresolved: "Nierozwiązane", all: "Wszystkie", empty: "Brak błędów do wyświetlenia." },
  en: { errorLabel: "Error", restore: "Restore", resolved: "Resolved", stackTrace: "Stack trace", title: "Application errors", subtitle: (n: number) => `Error events reported to the administrator. Unresolved: ${n}.`, unresolved: "Unresolved", all: "All", empty: "No errors to display." },
};

type ErrorEvent = {
  id: number;
  context: string;
  exception_class: string | null;
  message: string | null;
  backtrace: string | null;
  metadata: Record<string, unknown>;
  resolved: boolean;
  user_email: string | null;
  created_at: string;
};

type Props = {
  filter: "unresolved" | "all";
  unresolved_count: number;
  errors: ErrorEvent[];
  current_user: CurrentUser;
  products?: { id: number; name: string }[];
};

function initialsFrom(name: string): string {
  return name
    .split(" ")
    .map((word) => word.at(0) ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === "en" ? "en-GB" : "pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function toggleResolved(event: ErrorEvent, filter: string) {
  router.patch(`/admin/errors/${event.id}`, { resolved: !event.resolved, filter }, { preserveScroll: true });
}

function ErrorRow({ event, filter }: { event: ErrorEvent; filter: string }) {
  const t = pick(copy);
  return (
    <div className="panel" style={{ marginBottom: 10, opacity: event.resolved ? 0.6 : 1 }}>
      <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 11, color: "var(--muted-2)" }}>{formatDateTime(event.created_at)}</div>
            <div style={{ fontWeight: 650, color: "var(--ink)", marginTop: 2 }}>
              {event.exception_class ?? t.errorLabel} <span style={{ color: "var(--muted)", fontWeight: 400 }}>· {event.context}</span>
            </div>
            {event.user_email && (
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{event.user_email}</div>
            )}
          </div>
          <button
            type="button"
            className={"btn btn-sm " + (event.resolved ? "btn-ghost" : "btn-primary")}
            onClick={() => toggleResolved(event, filter)}
          >
            {event.resolved ? t.restore : t.resolved}
          </button>
        </div>

        {event.message && (
          <div style={{ fontSize: 13, color: "var(--ink)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {event.message}
          </div>
        )}

        {event.backtrace && (
          <details>
            <summary style={{ fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>{t.stackTrace}</summary>
            <pre className="mono" style={{ fontSize: 11, color: "var(--muted)", overflowX: "auto", marginTop: 6, whiteSpace: "pre-wrap" }}>
              {event.backtrace}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default function AdminErrors({ filter, unresolved_count, errors, current_user, products = [] }: Props) {
  const t = pick(copy);
  const initials = initialsFrom(current_user.name);

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 920 }}>
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.subtitle(unresolved_count)}</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <Link href="/admin/errors?filter=unresolved" className={"btn btn-sm " + (filter === "unresolved" ? "btn-primary" : "btn-ghost")}>
            {t.unresolved}
          </Link>
          <Link href="/admin/errors?filter=all" className={"btn btn-sm " + (filter === "all" ? "btn-primary" : "btn-ghost")}>
            {t.all}
          </Link>
        </div>

        {errors.length === 0 ? (
          <div className="panel">
            <div className="panel-body" style={{ fontSize: 13, color: "var(--muted)" }}>
              {t.empty}
            </div>
          </div>
        ) : (
          errors.map((event) => <ErrorRow key={event.id} event={event} filter={filter} />)
        )}
      </div>
    </AppShell>
  );
}
